import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ELIG_TOKENS,
  READINESS_FILLED,
  READINESS_TONE,
} from '@features/eligibility-checks/theme/readinessTokens';
import { ProviderBadge } from '@jotpsych/ui';
import { Box, ButtonBase, Popover, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import type { AppointmentResponse } from '@store/generated/appointments';
import { format } from 'date-fns';

import {
  type ClusteredCalendarEvent,
  type ClusterReadinessRollup,
  summarizeClusterReadiness,
} from '../clusterDensity';
import type { CalendarEvent } from '../types';
import { getCalendarSessionCue } from '../utils/sessionCue';

/**
 * Capacity-cluster card: stands in for an hour bucket of overlapping
 * appointments in the practice-wide week view. Header carries the count and
 * an action-state rollup; the body shows provider chips and a mini agenda.
 * Clicking opens a popover listing every member; selecting a member opens
 * that appointment's detail view.
 *
 * Deliberately pure (no store/analytics hooks) — the CalendarWidget injects
 * provider-name resolution and tracking callbacks — so it stays portable
 * into Storybook.
 */

interface ClusterEventCardProps {
  event: ClusteredCalendarEvent;
  /** Resolve a display name for a member's provider. */
  providerNameOf: (appointment: AppointmentResponse) => string | null;
  /** Fired when the member list popover is opened. */
  onClusterOpened?: (memberCount: number, providerCount: number) => void;
  /** Fired when a member row is chosen (open its appointment detail). */
  onAppointmentSelect?: (appointmentId: string, memberCount: number) => void;
}

const ROLLUP_STYLES: Array<{
  key: keyof ClusterReadinessRollup;
  label: string;
  color: string;
  filled: boolean;
}> = [
  { key: 'blocked', label: 'blocked', color: ELIG_TOKENS.red, filled: true },
  { key: 'attention', label: 'attention', color: ELIG_TOKENS.amber, filled: false },
  { key: 'collect', label: 'collect', color: ELIG_TOKENS.green, filled: false },
];

const RollupPill = ({
  count,
  label,
  color,
  filled,
}: {
  count: number;
  label: string;
  color: string;
  filled: boolean;
}) => (
  <Stack
    direction="row"
    spacing={0.4}
    sx={{
      alignItems: 'center',
      flexShrink: 0,
      px: 0.5,
      py: 0.1,
      borderRadius: '4px',
      bgcolor: 'rgba(20, 22, 26, 0.04)',
    }}
  >
    <Box
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        boxSizing: 'border-box',
        flexShrink: 0,
        ...(filled
          ? { bgcolor: color }
          : { border: `1.5px solid ${color}`, bgcolor: 'transparent' }),
      }}
    />
    <Typography component="span" sx={{ fontSize: 10, fontWeight: 700, color, lineHeight: 1.2 }}>
      {count} {label}
    </Typography>
  </Stack>
);

const memberReadinessColor = (member: CalendarEvent): { color: string; filled: boolean } | null => {
  const readiness = member.resource.block_state?.readiness ?? null;
  if (!readiness) return null;
  return { color: ELIG_TOKENS[READINESS_TONE[readiness]], filled: READINESS_FILLED[readiness] };
};

const memberPatientName = (member: CalendarEvent): string =>
  member.resource.patient?.name ?? member.title.split(' - ')[0] ?? member.title;

const isMemberCancelled = (member: CalendarEvent): boolean =>
  member.resource.block_state?.reality === 'cancelled' ||
  member.resource.confirmation_status === 'cancelled';

const MemberRow = ({
  member,
  providerName,
  onSelect,
}: {
  member: CalendarEvent;
  providerName: string | null;
  onSelect?: () => void;
}) => {
  const readiness = memberReadinessColor(member);
  const cancelled = isMemberCancelled(member);
  const sessionCue = getCalendarSessionCue(member.resource);

  return (
    <ButtonBase
      onClick={onSelect}
      // Unique per row so a screen reader can distinguish members in a busy
      // popover. Patient name + start time are already on screen, so this
      // adds no new PHI to the DOM.
      aria-label={`Open ${memberPatientName(member)} – ${format(member.start, 'h:mm a')}`}
      sx={{
        display: 'flex',
        width: '100%',
        gap: 0.75,
        alignItems: 'center',
        justifyContent: 'flex-start',
        minWidth: 0,
        px: 0.75,
        py: 0.5,
        borderRadius: '5px',
        textAlign: 'left',
        '&:hover': { bgcolor: 'rgba(28, 30, 133, 0.05)' },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 10.5,
          color: ELIG_TOKENS.inkSoft,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          width: 92,
          flexShrink: 0,
        }}
      >
        {format(member.start, 'h:mm')}–{format(member.end, 'h:mm a')}
      </Typography>
      <ProviderBadge name={providerName} />
      <Typography
        component="span"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: ELIG_TOKENS.ink,
          minWidth: 0,
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textDecoration: cancelled ? 'line-through' : 'none',
          opacity: cancelled ? 0.6 : 1,
        }}
      >
        {memberPatientName(member)}
      </Typography>
      {sessionCue && (
        <Tooltip title={sessionCue.label} placement="top" arrow>
          <sessionCue.Icon
            aria-label={sessionCue.label}
            sx={{
              fontSize: 12,
              color: sessionCue.useJotMeet ? ELIG_TOKENS.midnight : ELIG_TOKENS.inkSoft,
              opacity: sessionCue.useJotMeet ? 0.95 : 0.8,
            }}
          />
        </Tooltip>
      )}
      {readiness && (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            boxSizing: 'border-box',
            flexShrink: 0,
            ...(readiness.filled
              ? { bgcolor: readiness.color }
              : { border: `1.5px solid ${readiness.color}`, bgcolor: 'transparent' }),
          }}
        />
      )}
    </ButtonBase>
  );
};

// Hover timing matches the CalendarV2 appointment popover constants
// (CALENDAR_V2_POPOVER_OPEN_MS / CLOSE_MS). Defined locally so this card
// stays importable without dragging in the full event-component module.
const HOVER_OPEN_MS = 450;
const HOVER_CLOSE_MS = 100;

export const ClusterEventCard: React.FC<ClusterEventCardProps> = ({
  event,
  providerNameOf,
  onClusterOpened,
  onAppointmentSelect,
}) => {
  // Hover (after a short rest) peeks the member list, same as hovering an
  // individual appointment card; moving away closes it. Clicking pins it.
  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const members = useMemo(() => event.members ?? [], [event.members]);

  // Chip strip dedups by display name (two same-named providers shouldn't
  // render two identical chips).
  const providerNames = useMemo(
    () => [...new Set(members.map(member => providerNameOf(member.resource) ?? 'Unassigned'))],
    [members, providerNameOf],
  );
  // Analytics counts distinct providers by ID so same-named providers count
  // separately — matches "distinct providers" in the event dictionary.
  const distinctProviderCount = useMemo(
    () => new Set(members.map(member => member.resource.provider_id ?? 'unassigned')).size,
    [members],
  );
  const rollup = useMemo(() => summarizeClusterReadiness(members), [members]);
  const rollupPills = ROLLUP_STYLES.filter(({ key }) => rollup[key] > 0);

  const timeLabel = `${format(event.start, 'h:mm')}–${format(event.end, 'h:mm a')}`;

  const clearTimers = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  useEffect(() => clearTimers, []);

  const closePopover = () => {
    clearTimers();
    setAnchorEl(null);
    setIsPinned(false);
  };

  const handleCardMouseEnter = () => {
    if (isTouch) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (anchorEl || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setAnchorEl(cardRef.current);
    }, HOVER_OPEN_MS);
  };

  const scheduleHoverClose = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (isPinned || !anchorEl) return;
    closeTimerRef.current = setTimeout(closePopover, HOVER_CLOSE_MS);
  };

  const cancelHoverClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopover = (clickEvent: React.MouseEvent<HTMLElement>) => {
    clickEvent.stopPropagation();
    clearTimers();
    setIsPinned(true);
    setAnchorEl(cardRef.current);
    onClusterOpened?.(members.length, distinctProviderCount);
  };

  const selectMember = (member: CalendarEvent) => {
    closePopover();
    onAppointmentSelect?.(member.resource.appointment_id, members.length);
  };

  return (
    <>
      <Box
        ref={cardRef}
        data-cluster-event-card="true"
        onClick={openPopover}
        onMouseEnter={handleCardMouseEnter}
        onMouseLeave={scheduleHoverClose}
        role="button"
        aria-label={`${members.length} appointments starting ${timeLabel}. Open list`}
        sx={{
          height: '100%',
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          borderRadius: '6px',
          bgcolor: '#FCFCFF',
          border: '1px solid rgba(28, 30, 133, 0.22)',
          borderTop: `3px solid ${ELIG_TOKENS.midnight}`,
          color: ELIG_TOKENS.ink,
          px: 0.75,
          py: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.4,
          cursor: 'pointer',
          transition: 'box-shadow 120ms ease-out',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.16)',
            position: 'relative',
            zIndex: 5,
          },
          '@container (height < 30px)': { py: 0.1, justifyContent: 'center' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontSize: 12.5,
              fontWeight: 800,
              color: ELIG_TOKENS.midnight,
              whiteSpace: 'nowrap',
            }}
          >
            {members.length} appts
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: 10,
              color: ELIG_TOKENS.inkSoft,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            starting {timeLabel}
          </Typography>
        </Box>

        {rollupPills.length > 0 && (
          <Stack
            direction="row"
            spacing={0.4}
            sx={{
              flexWrap: 'nowrap',
              overflow: 'hidden',
              '@container (height < 46px)': { display: 'none' },
            }}
          >
            {rollupPills.map(({ key, label, color, filled }) => (
              <RollupPill
                key={key}
                count={rollup[key]}
                label={label}
                color={color}
                filled={filled}
              />
            ))}
          </Stack>
        )}

        <Stack
          direction="row"
          spacing={0.25}
          sx={{
            flexWrap: 'nowrap',
            overflow: 'hidden',
            '@container (height < 64px)': { display: 'none' },
          }}
        >
          {providerNames.slice(0, 6).map(name => (
            <ProviderBadge key={name} name={name === 'Unassigned' ? null : name} />
          ))}
          {providerNames.length > 6 && (
            <Typography
              component="span"
              sx={{ fontSize: 10, color: ELIG_TOKENS.inkSoft, alignSelf: 'center' }}
            >
              +{providerNames.length - 6}
            </Typography>
          )}
        </Stack>

        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            '@container (height < 88px)': { display: 'none' },
            '&::after': {
              content: '""',
              position: 'absolute',
              insetInline: 0,
              bottom: 0,
              height: 12,
              background: 'linear-gradient(to bottom, rgba(252,252,255,0), #FCFCFF)',
              pointerEvents: 'none',
            },
          }}
        >
          {members.map(member => {
            const cancelled = isMemberCancelled(member);
            const readiness = memberReadinessColor(member);
            const sessionCue = getCalendarSessionCue(member.resource);
            return (
              <Box
                key={member.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, py: '1px' }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10,
                    color: ELIG_TOKENS.inkSoft,
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                    width: 30,
                  }}
                >
                  {format(member.start, 'h:mm')}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    minWidth: 0,
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: cancelled ? 'line-through' : 'none',
                    opacity: cancelled ? 0.6 : 1,
                  }}
                >
                  {memberPatientName(member)}
                </Typography>
                {sessionCue && (
                  <Tooltip title={sessionCue.label} placement="top" arrow>
                    <sessionCue.Icon
                      aria-label={sessionCue.label}
                      sx={{
                        fontSize: 10,
                        color: sessionCue.useJotMeet ? ELIG_TOKENS.midnight : ELIG_TOKENS.inkSoft,
                      }}
                    />
                  </Tooltip>
                )}
                {readiness && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      boxSizing: 'border-box',
                      flexShrink: 0,
                      ...(readiness.filled
                        ? { bgcolor: readiness.color }
                        : { border: `1.5px solid ${readiness.color}`, bgcolor: 'transparent' }),
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={clickEvent => clickEvent.stopPropagation()}
        // Hover-opened popovers must not steal focus or lock scroll; the
        // root passes pointer events through until the user pins it with a
        // click, so the rest of the calendar stays interactive.
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        disableScrollLock
        sx={isPinned ? undefined : { pointerEvents: 'none' }}
        slotProps={{
          paper: {
            onMouseEnter: cancelHoverClose,
            onMouseLeave: scheduleHoverClose,
            sx: {
              pointerEvents: 'auto',
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14)',
            },
          },
        }}
      >
        <Box sx={{ width: 360, maxWidth: '90vw', maxHeight: 440, overflow: 'auto', p: 1.25 }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline', px: 0.75, mb: 0.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: ELIG_TOKENS.midnight }}>
              {members.length} appointments
            </Typography>
            <Typography sx={{ fontSize: 11, color: ELIG_TOKENS.inkSoft }}>
              starting {timeLabel}
            </Typography>
          </Stack>
          {rollupPills.length > 0 && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ px: 0.75, mb: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}
            >
              {rollupPills.map(({ key, label, color, filled }) => (
                <RollupPill
                  key={key}
                  count={rollup[key]}
                  label={label}
                  color={color}
                  filled={filled}
                />
              ))}
              {rollup.cancelled > 0 && (
                <RollupPill
                  count={rollup.cancelled}
                  label="cancelled"
                  color={ELIG_TOKENS.inkSoft}
                  filled
                />
              )}
            </Stack>
          )}
          <Stack spacing={0}>
            {members.map(member => (
              <MemberRow
                key={member.id}
                member={member}
                providerName={providerNameOf(member.resource)}
                onSelect={() => selectMember(member)}
              />
            ))}
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default ClusterEventCard;
